import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { SEO } from "@/lib/seo";
import {
  API, SERVICES, PROBLEMS, SOLUTIONS, METHOD_STEPS,
  TESTIMONIALS_DATA, LOCATIONS, SOCIAL_LINKS, WHATSAPP_NUMBER,
  CONTACT_EMAIL, TEAM_ITEMS
} from "@/lib/constants";
import {
  FileCheck, Compass, BarChart3, Phone, Mail, MapPin,
  Facebook, Instagram, Linkedin, Menu, X, Star, ArrowRight,
  CheckCircle, Users, Clock, Award, ChevronRight, MessageCircle,
  Building, Target, TrendingUp, Shield, Play, XCircle, Zap,
  Eye, Lock, AlertTriangle, Sparkles, ArrowDown, Edit, Trash2,
  Plus, Save, LogOut, Image, Download, ShoppingCart, FileSpreadsheet,
  HelpCircle, ChevronDown, CircleDollarSign, CalendarCheck, PieChart,
  FileText, Wallet, BadgePercent, Sparkle, RefreshCw, ShieldCheck, BookOpen
} from "lucide-react";

const PrivacyPolicyPage = () => (
  <main className="pt-20" data-testid="privacy-policy-page">
    <PageSEO {...SEO.privacyPolicy} />
    <section className="py-24 bg-[#FAF8F5]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <h1 className="font-heading text-5xl text-[#0D1527] mb-8">PRIVACY POLICY</h1>
        <div className="prose prose-invert max-w-none space-y-6 text-[#4A5568]">
          <p className="text-[#64708A] text-sm">Ultimo aggiornamento: Marzo 2026</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">1. Titolare del Trattamento</h2>
          <p>Il Titolare del trattamento dei dati personali e <strong className="text-[#0D1527]">SC Dottori Commercialisti SRL STP</strong>, con sede legale in Via Ponte Marino 41, 48121 Ravenna (RA), P.IVA IT02677200392, email: assistenza@casalisilvia.com (di seguito "Titolare").</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">2. Tipologia di Dati Raccolti</h2>
          <p>Il sito raccoglie i seguenti dati personali:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong className="text-[#0D1527]">Dati di contatto:</strong> nome, email, telefono e messaggio forniti volontariamente tramite il modulo di contatto.</li>
            <li><strong className="text-[#0D1527]">Dati di navigazione:</strong> dati tecnici acquisiti automaticamente (indirizzo IP, browser, pagine visitate).</li>
            <li><strong className="text-[#0D1527]">Email newsletter:</strong> indirizzo email fornito per l'iscrizione alla newsletter, anche tramite la richiesta dell'estratto gratuito del libro "Dai numeri alle decisioni" (in tal caso raccogliamo anche il nome, previo consenso esplicito, per finalita di contatto commerciale).</li>
            <li><strong className="text-[#0D1527]">Dati di interazione con la newsletter:</strong> in caso di iscrizione alla newsletter, rileviamo se le email inviate vengono aperte e se viene selezionato il collegamento all'articolo, tramite un pixel di tracciamento e link tracciati. Tali dati sono utilizzati esclusivamente per valutare l'interesse verso i contenuti inviati.</li>
          </ul>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">3. Finalita del Trattamento</h2>
          <p>I dati personali sono trattati per le seguenti finalita:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Rispondere alle richieste inviate tramite il modulo di contatto</li>
            <li>Invio di comunicazioni informative e articoli tramite newsletter (previo consenso)</li>
            <li>Analisi statistica delle interazioni con le email della newsletter (aperture e clic), al fine di migliorare i contenuti proposti</li>
            <li>Adempimento di obblighi di legge</li>
            <li>Miglioramento del servizio e dell'esperienza di navigazione</li>
          </ul>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">4. Base Giuridica</h2>
          <p>Il trattamento dei dati e basato su: consenso dell'interessato (art. 6.1.a GDPR), esecuzione di misure precontrattuali (art. 6.1.b GDPR), legittimo interesse del Titolare (art. 6.1.f GDPR), adempimento di obblighi legali (art. 6.1.c GDPR).</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">5. Conservazione dei Dati</h2>
          <p>I dati personali saranno conservati per il tempo strettamente necessario al perseguimento delle finalita per cui sono raccolti. I dati della newsletter, inclusi quelli relativi alle interazioni con le email, saranno conservati fino alla cancellazione dell'iscrizione, che puo essere effettuata in qualsiasi momento tramite il collegamento "Annulla iscrizione" presente in ogni email.</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">6. Diritti dell'Interessato</h2>
          <p>Ai sensi degli artt. 15-22 del GDPR, l'interessato ha diritto di:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Accedere ai propri dati personali</li>
            <li>Richiedere la rettifica o la cancellazione dei dati</li>
            <li>Opporsi al trattamento o richiederne la limitazione</li>
            <li>Richiedere la portabilita dei dati</li>
            <li>Revocare il consenso in qualsiasi momento</li>
          </ul>
          <p>Per esercitare i propri diritti, e possibile contattare il Titolare all'indirizzo: <strong className="text-gold">assistenza@casalisilvia.com</strong></p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">7. Comunicazione e Diffusione</h2>
          <p>I dati personali non saranno diffusi ne comunicati a terzi, salvo quanto previsto dalla legge o necessario per l'erogazione dei servizi richiesti. I dati potranno essere trattati da fornitori di servizi tecnici (hosting, email) in qualita di Responsabili del trattamento.</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">8. Trasferimento Dati</h2>
          <p>I dati potranno essere trasferiti verso paesi terzi nel rispetto delle garanzie previste dal GDPR (decisioni di adeguatezza, clausole contrattuali standard).</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">9. Contatti</h2>
          <p>Per qualsiasi informazione relativa alla presente Privacy Policy:</p>
          <p><strong className="text-[#0D1527]">SC Dottori Commercialisti SRL STP</strong><br />Via Ponte Marino 41, 48121 Ravenna (RA)<br />Email: assistenza@casalisilvia.com<br />P.IVA: IT02677200392</p>

          <div className="mt-12 pt-8 border-t border-[#E8E2D5]" data-testid="privacy-related-docs">
            <h2 className="font-heading text-2xl text-[#0D1527] mb-4">Documenti correlati</h2>
            <p className="text-[#4A5568] mb-4">Consulta anche le altre informative dello Studio:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><a href="/intelligenza-artificiale/" className="text-gold hover:underline">Come usiamo l'intelligenza artificiale</a> — dove, con quali dati e con quali controlli usiamo sistemi di AI nell'attività dello Studio (Legge 132/2025, Codice deontologico, Regolamento UE 2024/1689).</li>
              <li><Link to="/cookie-policy" className="text-gold hover:underline">Cookie Policy</Link> — tipologie di cookie utilizzati e come gestire le preferenze.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </main>
);

// Cookie Policy Page

export { PrivacyPolicyPage };
